import {
  BriefcaseBusiness,
  BusFront,
  CalendarDays,
  Code2,
  ContactRound,
  Landmark,
  MapPinned,
  Megaphone,
  ShoppingBag,
  UsersRound,
  Wheat,
} from 'lucide-react';

export const translated = (key, label, options = {}) => ({ key, label, type: 'translated', ...options });
export const category = (key, label, options = {}) => ({ key, label, type: 'category', ...options });
export const text = (key, label, options = {}) => ({ key, label, type: 'text', ...options });
export const textarea = (key, label, options = {}) => ({ key, label, type: 'textarea', ...options });
export const image = (key, label, options = {}) => ({ key, label, type: 'image', ...options });
export const toggle = (key, label, options = {}) => ({ key, label, type: 'toggle', ...options });
export const list = (key, label, options = {}) => ({ key, label, type: 'list', ...options });

export const notificationFields = (singularLabel = 'item') => [
  toggle('sendPush', `Send push notification on first publish (${singularLabel})`),
  toggle('sendUpdateNotification', 'Send update notification on this save'),
  text('notificationTitle', 'Push title override', {
    hint: 'Optional. Blank hoy to auto-generated title use thase.',
  }),
  textarea('notificationText', 'Push message override', {
    hint: 'Optional. Blank hoy to auto-generated message use thase.',
  }),
];

export const MODULES = {
  businesses: {
    label: 'Businesses',
    singular: 'Business',
    collection: 'businesses',
    idPrefix: 'b',
    icon: BriefcaseBusiness,
    color: '#2f855a',
    fields: [
      translated('name', 'Business name', { required: true }),
      category('category', 'Category', { required: true, storage: 'translated' }),
      translated('city', 'City', { required: true }),
      translated('desc', 'Description', { multiline: true }),
      toggle('featured', 'Special business — always show at top'),
      image('image', 'Cover image'),
      text('phone', 'Phone number'),
      text('whatsapp', 'WhatsApp number', { hint: 'Country code sathe, example: 919998414441' }),
      text('website', 'Website URL'),
      translated('address', 'Address'),
      text('locationUrl', 'Google Maps URL'),
      list('owners', 'Business owners', {
        itemLabel: 'Owner',
        itemFields: [
          translated('name', 'Owner name', { required: true }),
          translated('role', 'Role'),
          image('photo', 'Owner photo'),
          text('phone', 'Phone number'),
        ],
      }),
      ...notificationFields('Business'),
    ],
  },
  marketplaceListings: {
    label: 'Buy & Sell',
    singular: 'Listing',
    collection: 'marketplaceListings',
    idPrefix: 'sale',
    icon: ShoppingBag,
    color: '#3f8a31',
    fields: [
      translated('name', 'Product name', { required: true }),
      category('category', 'Category', {
        required: true,
        storage: 'translated',
        initialCategories: [
          { value: 'food', label: { gu: 'ખાદ્ય વસ્તુઓ', hi: 'खाद्य सामग्री', en: 'Food Items' } },
          { value: 'household', label: { gu: 'ઘરેલુ વસ્તુઓ', hi: 'घरेलू सामान', en: 'Household' } },
          { value: 'agriculture', label: { gu: 'ખેતી સંબંધિત', hi: 'खेती संबंधित', en: 'Agriculture' } },
          { value: 'livestock', label: { gu: 'પશુપાલન', hi: 'પशुपालन', en: 'Livestock' } },
          { value: 'electronics', label: { gu: 'ઇલેક્ટ્રોનિક્સ', hi: 'ઇલેક્ટ્રોનિક્સ', en: 'Electronics' } },
          { value: 'furniture', label: { gu: 'ફર્નિચર', hi: 'ફર્નિચર', en: 'Furniture' } },
          { value: 'other', label: { gu: 'અન્ય', hi: 'અન્ય', en: 'Other' } },
        ],
      }),
      translated('desc', 'Description', { multiline: true }),
      { key: 'price', label: 'Price', type: 'number', min: 0, required: true },
      text('priceUnit', 'Price unit', { required: true, placeholder: 'KG / Piece / Litre', hint: 'Example: KG, Piece, Litre, Quintal' }),
      { key: 'quantity', label: 'Available quantity', type: 'number', min: 0 },
      text('quantityUnit', 'Quantity unit', { placeholder: 'KG / Piece / Litre' }),
      image('image', 'Product photo'),
      translated('sellerName', 'Seller name', { required: true }),
      text('phone', 'Seller phone number', { required: true }),
      text('whatsapp', 'Seller WhatsApp number', { hint: 'Country code sathe, example: 919998414441' }),
      translated('sellerLocation', 'Seller origin location', { required: true }),
      text('locationUrl', 'Google Maps URL'),
      toggle('sold', 'Mark as sold'),
      ...notificationFields('Listing'),
      { key: 'sortOrder', label: 'Display order', type: 'number', min: 1 },
    ],
  },
  announcements: {
    label: 'Village Announcements',
    singular: 'Announcement',
    collection: 'announcements',
    idPrefix: 'ann',
    icon: Megaphone,
    color: '#c56a18',
    fields: [
      translated('title', 'Announcement title', { required: true }),
      {
        key: 'category',
        label: 'Category',
        type: 'select',
        required: true,
        options: [
          ['general', 'General'],
          ['panchayat', 'Gram Panchayat'],
          ['water', 'Water Supply'],
          ['electricity', 'Electricity'],
          ['health', 'Health'],
          ['education', 'Education'],
          ['scheme', 'Government Scheme'],
          ['event', 'Event / Camp'],
          ['emergency', 'Emergency'],
          ['other', 'Other'],
        ],
      },
      {
        key: 'priority',
        label: 'Priority',
        type: 'select',
        required: true,
        options: [
          ['normal', 'Normal'],
          ['important', 'Important'],
          ['emergency', 'Emergency'],
        ],
      },
      translated('description', 'Description', { required: true, multiline: true }),
      text('image', 'Cover image URL', {
        inputType: 'url',
        placeholder: 'https://…',
        hint: 'Optional. Cloudinary/HTTPS image URL paste karo.',
      }),
      translated('publisherName', 'Published by'),
      text('publishedAt', 'Publish date & time', {
        inputType: 'datetime-local',
        hint: 'Blank hoy to app created/updated time fallback tarike use karse.',
      }),
      text('expiresAt', 'Expiry date & time', {
        inputType: 'datetime-local',
        hint: 'Optional. Expiry pachhi mobile app active list mathi automatic hide karse.',
      }),
      toggle('showOnHome', 'Show as Home announcement banner'),
      toggle('isPinned', 'Pin announcement to top'),
      toggle('sendPush', 'Send push notification on first publish'),
      toggle('sendUpdateNotification', 'Send update notification on this save'),
      text('notificationTitle', 'Push title override', {
        hint: 'Optional. Blank hoy to Gujarati announcement title use thase.',
      }),
      textarea('notificationText', 'Push message override', {
        hint: 'Optional. Blank hoy to Gujarati description mathi short text use thase.',
      }),
      { key: 'sortOrder', label: 'Display order', type: 'number', min: 1 },
    ],
  },
  events: {
    label: 'Events',
    singular: 'Event',
    collection: 'events',
    idPrefix: 'e',
    icon: CalendarDays,
    color: '#7c3aed',
    fields: [
      translated('name', 'Event name', { required: true }),
      text('date', 'Event date', { inputType: 'date', required: true }),
      translated('desc', 'Description', { multiline: true }),
      image('cover', 'Cover image'),
      list('photos', 'Gallery photos', { primitive: true, itemType: 'image', itemLabel: 'Photo' }),
      text('external', 'External gallery URL'),
      ...notificationFields('Event'),
    ],
  },
  contacts: {
    label: 'Important Contacts',
    singular: 'Contact',
    collection: 'contacts',
    idPrefix: 'c',
    icon: ContactRound,
    color: '#2563eb',
    fields: [
      translated('name', 'Person / service name', { required: true }),
      translated('role', 'Role', { required: true }),
      translated('department', 'Department'),
      text('phone', 'Phone number', { required: true }),
      text('whatsapp', 'WhatsApp number'),
      image('photo', 'Photo'),
      category('category', 'Category', {
        storage: 'text',
        initialCategories: [
          { value: 'government', label: { gu: 'સરકારી', hi: 'सरकारी', en: 'Government' } },
          { value: 'health', label: { gu: 'આરોગ્ય', hi: 'स्वास्थ्य', en: 'Health' } },
          { value: 'education', label: { gu: 'શિક્ષણ', hi: 'शिक्षा', en: 'Education' } },
          { value: 'services', label: { gu: 'સેવાઓ', hi: 'सेवाएँ', en: 'Services' } },
          { value: 'other', label: { gu: 'અન્ય', hi: 'अन्य', en: 'Other' } },
        ],
      }),
      ...notificationFields('Contact'),
      { key: 'priority', label: 'Priority', type: 'number', min: 1 },
    ],
  },
  busSchedules: {
    label: 'Bus Schedule',
    singular: 'Bus route',
    collection: 'busSchedules',
    idPrefix: 'bus',
    icon: BusFront,
    color: '#d97706',
    fields: [
      translated('from', 'From', { required: true }),
      translated('to', 'To', { required: true }),
      list('times', 'Departure times', { primitive: true, itemType: 'text', itemLabel: 'Time', placeholder: '07:30 AM' }),
      ...notificationFields('Bus route'),
    ],
  },
  apmcMarkets: {
    label: 'APMC Markets',
    singular: 'APMC market',
    collection: 'apmcMarkets',
    idPrefix: 'apmc',
    icon: Wheat,
    color: '#4f8b2f',
    fields: [
      translated('name', 'Display name', { required: true }),
      text('marketKey', 'Market key', {
        required: true,
        hint: 'Stable lowercase key, example: kalawad, rajkot, gondal',
      }),
      text('state', 'API state filter', { required: true, placeholder: 'Gujarat' }),
      list('sourceMarkets', 'Official API market names', {
        primitive: true,
        itemType: 'text',
        itemLabel: 'Market name',
        placeholder: 'Example: Rajkot APMC',
      }),
      { key: 'sortOrder', label: 'Display order', type: 'number', min: 1 },
    ],
  },
  places: {
    label: 'Memorable Places',
    singular: 'Place',
    collection: 'places',
    idPrefix: 'p',
    icon: MapPinned,
    color: '#0891b2',
    fields: [
      translated('name', 'Place name', { required: true }),
      category('category', 'Category', { storage: 'translated' }),
      translated('desc', 'Description', { multiline: true }),
      image('cover', 'Cover image'),
      list('photos', 'Gallery photos', { primitive: true, itemType: 'image', itemLabel: 'Photo' }),
      translated('location', 'Location'),
      text('map', 'Google Maps URL'),
      ...notificationFields('Place'),
    ],
  },
  managers: {
    label: 'Content Managers',
    singular: 'Content manager',
    collection: 'managers',
    idPrefix: 'm',
    icon: UsersRound,
    color: '#db2777',
    fields: [
      translated('name', 'Name', { required: true }),
      translated('role', 'Role', { required: true }),
      text('phone', 'Phone number'),
      text('instagram', 'Instagram username'),
      image('photo', 'Photo'),
    ],
  },
  developers: {
    label: 'Developer Details',
    singular: 'Developer',
    collection: 'developers',
    idPrefix: 'd',
    icon: Code2,
    color: '#4f46e5',
    fields: [
      translated('name', 'Name', { required: true }),
      translated('role', 'Role'),
      translated('bio', 'Biography', { multiline: true }),
      text('phone', 'Phone number'),
      text('email', 'Email address', { inputType: 'email' }),
      text('instagram', 'Instagram username'),
      image('photo', 'Photo'),
    ],
  },
};

export const VILLAGE_MODULE = {
  label: 'About Village',
  singular: 'Village profile',
  collection: 'village',
  documentId: 'profile',
  icon: Landmark,
  fields: [
    translated('name', 'Village name', { required: true }),
    translated('location', 'Location', { required: true }),
    translated('heroTitle', 'Hero title', { required: true }),
    translated('heroText', 'Hero subtitle'),
    translated('intro', 'Village introduction', { multiline: true }),
    image('heroImage', 'Hero image'),
    list('facts', 'Village facts', {
      itemLabel: 'Fact',
      itemFields: [
        translated('label', 'Label', { required: true }),
        translated('value', 'Value', { required: true }),
      ],
    }),
  ],
};

export const MODULE_KEYS = ['village', ...Object.keys(MODULES)];
export const ACTION_KEYS = ['view', 'create', 'edit', 'publish', 'archive', 'delete', 'reorder', 'media', 'notify'];

export const emptyPermissions = () => Object.fromEntries(
  [...MODULE_KEYS, 'adminUsers', 'auditLogs'].map((key) => [
    key,
    Object.fromEntries(ACTION_KEYS.map((action) => [action, false])),
  ]),
);

export function translatedValue(value, language = 'en') {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value[language] || value.en || value.gu || value.hi || '';
  }
  return String(value ?? '');
}

export function recordTitle(record, moduleKey) {
  if (moduleKey === 'busSchedules') {
    return `${translatedValue(record.from)} → ${translatedValue(record.to)}`;
  }
  if (moduleKey === 'announcements') return translatedValue(record.title) || record.id || 'Untitled announcement';
  return translatedValue(record.name || record.heroTitle || record.role) || record.id || 'Untitled';
}

export function recordSubtitle(record, moduleKey) {
  if (moduleKey === 'businesses') {
    const categoryLabel = translatedValue(record.category);
    return record.featured ? `Special • ${categoryLabel}` : categoryLabel;
  }
  if (moduleKey === 'marketplaceListings') {
    const categoryLabel = translatedValue(record.category);
    const price = record.price ?? '';
    const unit = record.priceUnit ? `/${record.priceUnit}` : '';
    return `${record.sold ? 'Sold' : 'Available'} • ${categoryLabel}${price !== '' ? ` • ₹${price}${unit}` : ''}`;
  }
  if (moduleKey === 'announcements') {
    return `${record.priority || 'normal'} • ${record.category || 'general'}${record.showOnHome ? ' • Home banner' : ''}`;
  }
  if (moduleKey === 'events') return record.date || '';
  if (moduleKey === 'contacts') return translatedValue(record.role);
  if (moduleKey === 'apmcMarkets') return (record.sourceMarkets || []).join(', ');
  if (moduleKey === 'places') return translatedValue(record.location || record.category);
  if (moduleKey === 'managers' || moduleKey === 'developers') return translatedValue(record.role);
  if (moduleKey === 'busSchedules') return (record.times || []).join(', ');
  return '';
}

export const PUSH_SUPPORTED_MODULES = [
  'announcements',
  'businesses',
  'busSchedules',
  'marketplaceListings',
  'events',
  'contacts',
  'places',
];

export function generateRecordNotificationPreview(record, moduleKey) {
  if (!record) return { title: '', body: '', screen: '', targetId: '', imageUrl: '' };

  const customTitle = String(record.notificationTitle || '').trim();
  const customBody = String(record.notificationText || '').trim();
  const rawImage = record.image || record.cover || record.photo;
  const imageUrl = typeof rawImage === 'string' ? rawImage : (rawImage?.url || '');

  let defaultTitle = '';
  let defaultBody = '';
  let screen = '';

  switch (moduleKey) {
    case 'businesses': {
      const name = translatedValue(record.name, 'gu') || translatedValue(record.name, 'en') || 'નવો વ્યવસાય';
      const cat = translatedValue(record.category, 'gu') || translatedValue(record.category, 'en');
      const city = translatedValue(record.city, 'gu') || translatedValue(record.city, 'en');
      defaultTitle = `નવો વ્યવસાય: ${name}`;
      const parts = [cat, city].filter(Boolean);
      defaultBody = parts.length
        ? `${parts.join(' • ')} - આપણી એપમાં નવો વ્યવસાય ઉમેરાયો છે.`
        : 'આપણી એપમાં નવો વ્યવસાય ઉમેરાયો છે. વિગતો જુઓ.';
      screen = 'Businesses';
      break;
    }
    case 'busSchedules': {
      const from = translatedValue(record.from, 'gu') || translatedValue(record.from, 'en');
      const to = translatedValue(record.to, 'gu') || translatedValue(record.to, 'en');
      const times = Array.isArray(record.times) ? record.times.filter(Boolean).join(', ') : '';
      defaultTitle = `નવું બસ સમયપત્રક: ${from} થી ${to}`;
      defaultBody = times ? `ઉપડવાનો સમય: ${times} - નવી બસ સેવાની વિગત જુઓ.` : 'બસ રૂટની નવી સમયપત્રક વિગત ઉપલબ્ધ છે.';
      screen = 'BusSchedule';
      break;
    }
    case 'marketplaceListings': {
      const name = translatedValue(record.name, 'gu') || translatedValue(record.name, 'en') || 'નવી વસ્તુ';
      const price = record.price !== undefined && record.price !== '' ? `₹${record.price}${record.priceUnit ? `/${record.priceUnit}` : ''}` : '';
      const seller = translatedValue(record.sellerName, 'gu') || translatedValue(record.sellerName, 'en');
      const loc = translatedValue(record.sellerLocation, 'gu') || translatedValue(record.sellerLocation, 'en');
      defaultTitle = `ખરીદ-વેચાણ: ${name}`;
      const sellerInfo = [seller, loc ? `(${loc})` : ''].filter(Boolean).join(' ');
      defaultBody = [price ? `કિંમત: ${price}` : '', sellerInfo ? `વિક્રેતા: ${sellerInfo}` : ''].filter(Boolean).join(' • ') || 'નવી ખરીદ-વેચાણ આઇટમ ઉમેરાઈ છે.';
      screen = 'MarketplaceDetail';
      break;
    }
    case 'events': {
      const name = translatedValue(record.name, 'gu') || translatedValue(record.name, 'en') || 'નવો કાર્યક્રમ';
      const date = record.date ? `તારીખ: ${record.date}` : '';
      defaultTitle = `નવો કાર્યક્રમ: ${name}`;
      defaultBody = date ? `${date} - ગામના નવા કાર્યક્રમની વિગતો જુઓ.` : 'ગામના નવા કાર્યક્રમની વિગતો જુઓ.';
      screen = 'EventDetail';
      break;
    }
    case 'contacts': {
      const name = translatedValue(record.name, 'gu') || translatedValue(record.name, 'en') || 'મહત્વનો સંપર્ક';
      const role = translatedValue(record.role, 'gu') || translatedValue(record.role, 'en');
      const dept = translatedValue(record.department, 'gu') || translatedValue(record.department, 'en');
      const phone = record.phone ? `ફોન: ${record.phone}` : '';
      defaultTitle = `મહત્વનો સંપર્ક: ${name}`;
      const roleDept = [role, dept ? `(${dept})` : ''].filter(Boolean).join(' ');
      defaultBody = [roleDept, phone].filter(Boolean).join(' - ') || 'મહત્વના સંપર્કની નવી માહિતી ઉમેરાઈ છે.';
      screen = 'Contacts';
      break;
    }
    case 'places': {
      const name = translatedValue(record.name, 'gu') || translatedValue(record.name, 'en') || 'જોવાલાયક સ્થળ';
      const loc = translatedValue(record.location, 'gu') || translatedValue(record.location, 'en');
      defaultTitle = `જોવાલાયક સ્થળ: ${name}`;
      defaultBody = loc ? `${loc} - સ્થળની વિગત અને ગેલેરી જુઓ.` : 'નવા જોવાલાયક સ્થળની માહિતી અને ગેલેરી જુઓ.';
      screen = 'PlaceDetail';
      break;
    }
    case 'announcements':
    default: {
      const title = translatedValue(record.title, 'gu') || translatedValue(record.title, 'en') || 'ગામ સૂચના';
      const desc = translatedValue(record.description, 'gu') || translatedValue(record.description, 'en') || 'નવી ગામ સૂચના ઉપલબ્ધ છે.';
      defaultTitle = `ગામ સૂચના: ${title}`;
      defaultBody = desc.length > 150 ? `${desc.slice(0, 147)}…` : desc;
      screen = 'AnnouncementDetail';
      break;
    }
  }

  return {
    title: customTitle || defaultTitle,
    body: customBody || defaultBody,
    imageUrl: typeof imageUrl === 'string' ? imageUrl : '',
    screen,
    targetId: record.id || '',
  };
}

